import type { Bundle, OperationOutcome, Resource } from "./types";

export interface FhirClientConfig {
  baseUrl: string;
  tokenProvider?: () => Promise<string | null>;
  onError?: (error: FhirError) => void;
  timeout?: number;
}

export interface SearchParams {
  [key: string]: string | number | boolean | (string | number | boolean)[] | undefined;
}

export class FhirError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly outcome?: OperationOutcome
  ) {
    super(message);
    this.name = "FhirError";
    Object.setPrototypeOf(this, FhirError.prototype);
  }
}

/**
 * FHIR R4 REST API Client
 *
 * Provides type-safe methods for interacting with any FHIR R4 compliant server.
 * Supports all standard FHIR REST operations including search, CRUD, and batch operations.
 *
 * @see https://www.hl7.org/fhir/http.html
 */
export class FhirClient {
  private baseUrl: string;
  private tokenProvider?: () => Promise<string | null>;
  private timeout: number;
  private onError?: (error: FhirError) => void;

  constructor(config: FhirClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, ""); // Remove trailing slash
    this.tokenProvider = config.tokenProvider;
    this.timeout = config.timeout ?? 30000;
    this.onError = config.onError;
  }

  /**
   * Execute FHIR search operation
   * @see https://www.hl7.org/fhir/search.html
   *
   * @example
   * const bundle = await client.search<Patient>('Patient', {
   *   _count: 10,
   *   name: 'John',
   *   birthdate: 'gt1990-01-01'
   * });
   */
  async search<T extends Resource>(resourceType: string, params?: SearchParams): Promise<Bundle<T>> {
    const queryString = this.buildSearchParams(params);
    const url = `${this.baseUrl}/${resourceType}${queryString}`;

    return this.request<Bundle<T>>("GET", url);
  }

  /**
   * Search for a single resource (returns first match or undefined)
   *
   * @example
   * const patient = await client.searchOne<Patient>('Patient', { identifier: '12345' });
   */
  async searchOne<T extends Resource>(resourceType: string, params?: SearchParams): Promise<T | undefined> {
    const searchParams = { ...params, _count: 1 };
    const bundle = await this.search<T>(resourceType, searchParams);
    return bundle.entry?.[0]?.resource as T | undefined;
  }

  /**
   * Read a single resource by ID
   * @see https://www.hl7.org/fhir/http.html#read
   *
   * @example
   * const patient = await client.read<Patient>('Patient', '123');
   */
  async read<T extends Resource>(resourceType: string, id: string): Promise<T> {
    const url = `${this.baseUrl}/${resourceType}/${id}`;
    return this.request<T>("GET", url);
  }

  /**
   * Read specific version of a resource
   * @see https://www.hl7.org/fhir/http.html#vread
   *
   * @example
   * const patient = await client.vread<Patient>('Patient', '123', '2');
   */
  async vread<T extends Resource>(resourceType: string, id: string, versionId: string): Promise<T> {
    const url = `${this.baseUrl}/${resourceType}/${id}/_history/${versionId}`;
    return this.request<T>("GET", url);
  }

  /**
   * Create a new resource
   * @see https://www.hl7.org/fhir/http.html#create
   *
   * @example
   * const created = await client.create<Patient>({
   *   resourceType: 'Patient',
   *   name: [{ given: ['John'], family: 'Doe' }]
   * });
   */
  async create<T extends Resource>(resource: T, extraHeaders?: Record<string, string>): Promise<T> {
    if (!resource.resourceType) {
      throw new FhirError("Resource must have resourceType");
    }

    const url = `${this.baseUrl}/${resource.resourceType}`;
    return this.request<T>("POST", url, resource, extraHeaders);
  }

  /**
   * Update an existing resource (full replacement)
   * @see https://www.hl7.org/fhir/http.html#update
   *
   * @example
   * const updated = await client.update<Patient>({
   *   resourceType: 'Patient',
   *   id: '123',
   *   name: [{ given: ['Jane'], family: 'Doe' }]
   * });
   */
  async update<T extends Resource>(resource: T, extraHeaders?: Record<string, string>): Promise<T> {
    if (!resource.resourceType || !resource.id) {
      throw new FhirError("Resource must have resourceType and id for update");
    }

    const url = `${this.baseUrl}/${resource.resourceType}/${resource.id}`;
    return this.request<T>("PUT", url, resource, extraHeaders);
  }

  /**
   * Patch a resource using JSON Patch operations
   * @see https://www.hl7.org/fhir/http.html#patch
   * @see https://jsonpatch.com/
   *
   * @example
   * const patched = await client.patch('Patient', '123', [
   *   { op: 'replace', path: '/name/0/given/0', value: 'Jane' }
   * ]);
   */
  async patch(
    resourceType: string,
    id: string,
    operations: Array<{
      op: "add" | "remove" | "replace" | "move" | "copy" | "test";
      path: string;
      value?: unknown;
      from?: string;
    }>,
    extraHeaders?: Record<string, string>
  ): Promise<Resource> {
    const url = `${this.baseUrl}/${resourceType}/${id}`;
    return this.request<Resource>("PATCH", url, operations, {
      "Content-Type": "application/json-patch+json",
      ...extraHeaders,
    });
  }

  /**
   * Delete a resource
   * @see https://www.hl7.org/fhir/http.html#delete
   *
   * @example
   * await client.delete('Patient', '123');
   */
  async delete(resourceType: string, id: string): Promise<void> {
    const url = `${this.baseUrl}/${resourceType}/${id}`;
    await this.request<void>("DELETE", url);
  }

  /**
   * Execute a FHIR batch/transaction bundle
   * @see https://www.hl7.org/fhir/http.html#transaction
   *
   * @example
   * const result = await client.batch<Patient>({
   *   resourceType: 'Bundle',
   *   type: 'batch',
   *   entry: [
   *     {
   *       request: { method: 'POST', url: 'Patient' },
   *       resource: { resourceType: 'Patient', name: [{ given: ['John'] }] }
   *     }
   *   ]
   * });
   */
  async batch<T extends Resource>(bundle: Bundle<T>, extraHeaders?: Record<string, string>): Promise<Bundle<T>> {
    const url = `${this.baseUrl}/`;
    return this.request<Bundle<T>>("POST", url, bundle, extraHeaders);
  }

  /**
   * Get resource history
   * @see https://www.hl7.org/fhir/http.html#history
   *
   * @example
   * const history = await client.history('Patient', '123');
   */
  async history<T extends Resource>(resourceType: string, id: string, params?: SearchParams): Promise<Bundle<T>> {
    const queryString = this.buildSearchParams(params);
    const url = `${this.baseUrl}/${resourceType}/${id}/_history${queryString}`;
    return this.request<Bundle<T>>("GET", url);
  }

  /**
   * Core HTTP request handler with authentication and error handling
   */
  private async request<T>(
    method: string,
    url: string,
    body?: unknown,
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/fhir+json",
      Accept: "application/fhir+json",
      ...extraHeaders,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Add authentication token if available — inside try so auth errors are caught
      if (this.tokenProvider) {
        const token = await this.tokenProvider();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      }

      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        cache: "no-store",
      });

      clearTimeout(timeoutId);

      // Handle non-2xx responses
      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      // Handle responses with no expected body — 204 No Content or any successful DELETE.
      // HealthLake returns 200 with a searchset Bundle on DELETE instead of 204,
      // so we skip JSON parsing for DELETE to avoid treating it as a failure.
      if (response.status === 204 || method === "DELETE") {
        return undefined as T;
      }

      return (await response.json()) as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof FhirError) {
        if (this.onError) {
          this.onError(error);
        }
        throw error;
      }

      // DOMException (AbortError / TimeoutError) may not extend Error in all environments
      const errorName = (error as { name?: unknown } | null)?.name;
      if (errorName === "AbortError" || errorName === "TimeoutError") {
        const timeoutError = new FhirError(`Request timeout after ${this.timeout}ms`);
        if (this.onError) {
          this.onError(timeoutError);
        }
        throw timeoutError;
      }

      const message = error instanceof Error ? error.message : "Unknown error";
      const networkError = new FhirError(`Network error: ${message}`);
      if (this.onError) {
        this.onError(networkError);
      }
      throw networkError;
    }
  }

  /**
   * Parse FHIR OperationOutcome from error response
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    let outcome: OperationOutcome | undefined;
    let serverMessage: string | undefined;

    try {
      const data = await response.json();
      if (data.resourceType === "OperationOutcome") {
        outcome = data as OperationOutcome;
      } else if (typeof data.message === "string") {
        // Non-OperationOutcome error (e.g. { statusCode: 409, message: "..." })
        serverMessage = data.message;
      }
    } catch {
      // Response may not be JSON
    }

    const message =
      outcome?.issue?.[0]?.diagnostics ??
      outcome?.issue?.[0]?.details?.text ??
      serverMessage ??
      `FHIR request failed with status ${response.status}`;

    throw new FhirError(message, response.status, outcome);
  }

  /**
   * Build query string from search parameters
   */
  private buildSearchParams(params?: SearchParams): string {
    if (!params || Object.keys(params).length === 0) {
      return "";
    }

    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }

    return `?${searchParams.toString()}`;
  }

  /**
   * Get the base URL for this client
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Check if client has authentication configured
   */
  hasAuthentication(): boolean {
    return this.tokenProvider !== undefined;
  }

  /**
   * Update the token provider (useful for updating auth state)
   */
  setTokenProvider(tokenProvider: () => Promise<string | null>): void {
    this.tokenProvider = tokenProvider;
  }

  /**
   * Clear the token provider (logout)
   */
  clearTokenProvider(): void {
    this.tokenProvider = undefined;
  }
}
